﻿import Select, { GroupBase, OnChangeValue, Props } from "react-select";
import { Controller, UseControllerProps } from "react-hook-form";
import { defaultStyles, formatOptionLabel, Option, Value } from "../AutoComplete";

type MultiSelectPropOverrides<V extends Value> = {
    onChange?: (newValue: V[]) => void;
    values: Value[] | null | undefined;
};
type ExpectedMultiPropOverrides<V extends Value> = Pick<Props<Option<V>, true, GroupBase<Option<V>>>, "onChange" | "value">;
type RemainingExpectedMultiProps<V extends Value> = Omit<Props<Option<V>, true, GroupBase<Option<V>>>, "onChange" | "value">;
type MultiSelectProps<V extends Value> = MultiSelectPropOverrides<V> & RemainingExpectedMultiProps<V>;

type MultiSelectControlledProps<V extends Value> = RemainingExpectedMultiProps<V> & UseControllerProps;

/**
 * This is a wrapper around react-select that provides a simpler api for multi-selects.
 * @param onChange
 * @param values
 * @param styles
 * @param props
 * @constructor
 */
export const MultiSelect = <V extends Value>({ onChange, values, styles, ...props }: MultiSelectProps<V>) => {
    const overrides: ExpectedMultiPropOverrides<V> = {};

    if (onChange) {
        overrides.onChange = (newValues: OnChangeValue<Option<V>, true>) => {
            onChange(newValues.map(newValue => newValue.value));
        };
    }
    if (values) {
        const filterFunction = (option: Option<V>) => values.includes(option.value);
        // @ts-ignore
        overrides.value = props.options?.filter(filterFunction);
    }
    return (
        <Select
            isMulti
            {...props}
            {...overrides}
            styles={{
                ...defaultStyles,
                ...styles,
            }}
            formatOptionLabel={formatOptionLabel}
        />
    );
};

/**
 * This is a convenience component that makes it easy to use the MultiSelect component with react-hook-form.
 * @param control
 * @param name
 * @param props
 * @param rules
 * @param defaultValue
 * @param disabled
 * @param shouldUnregister
 * @constructor
 */
export const MultiSelectControlled = <V extends Value>({
    control,
    name,
    rules,
    defaultValue,
    disabled,
    shouldUnregister,
    ...props
}: MultiSelectControlledProps<V>) => {
    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            defaultValue={defaultValue}
            disabled={disabled}
            shouldUnregister={shouldUnregister}
            render={({ field: { onChange, value } }) => {
                return <MultiSelect onChange={onChange} values={value} {...props} />;
            }}
        />
    );
};

type SingleSelectPropOverrides<V extends Value> = {
    onChange?: (newValue: V | undefined) => void;
    value: V | null | undefined;
};
type ExpectedSinglePropOverrides<V extends Value> = Pick<Props<Option<V>, false, GroupBase<Option<V>>>, "onChange" | "value">;
type RemainingExpectedSingleProps<V extends Value> = Omit<Props<Option<V>, false, GroupBase<Option<V>>>, "onChange" | "value">;
type SingleSelectProps<V extends Value> = SingleSelectPropOverrides<V> & RemainingExpectedSingleProps<V>;
type SingleSelectControlledProps<V extends Value> = RemainingExpectedSingleProps<V> & UseControllerProps;

/**
 * This is a wrapper around react-select that provides a simpler api for single-selects.
 * @param onChange
 * @param values
 * @param props
 * @constructor
 */
export const SingleSelect = <V extends Value>({ onChange, value, ...props }: SingleSelectProps<V>) => {
    const overrides: ExpectedSinglePropOverrides<V> = {};

    if (onChange) {
        overrides.onChange = (newValue: OnChangeValue<Option<V>, false>) => {
            onChange(newValue?.value);
        };
    }
    if (value) {
        // @ts-ignore
        overrides.value = props.options?.filter(option => value === option.value);
    }
    return <Select styles={defaultStyles} isMulti={false} {...props} {...overrides} formatOptionLabel={formatOptionLabel} />;
};

/**
 * This is a convenience component that makes it easy to use the SingleSelect component with react-hook-form.
 * @param control
 * @param name
 * @param props
 * @param rules
 * @param defaultValue
 * @param disabled
 * @param shouldUnregister
 * @constructor
 */
export const SingleSelectControlled = <V extends Value>({
    control,
    name,
    rules,
    defaultValue,
    disabled,
    shouldUnregister,
    ...props
}: SingleSelectControlledProps<V>) => {
    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            defaultValue={defaultValue}
            disabled={disabled}
            shouldUnregister={shouldUnregister}
            render={({ field: { onChange, value } }) => (
                <SingleSelect
                    // @ts-ignore
                    onChange={onChange}
                    value={value}
                    {...props}
                />
            )}
        />
    );
};
